import '@/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { CartProvider } from '@/context/CartContext';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SettingsProvider } from '@/context/SettingsContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CartDrawer } from '@/components/CartDrawer';
import { CookieBanner } from '@/components/CookieBanner';
import { AdminLayout } from '@/components/AdminLayout';
import { DocumentTitle } from '@/hooks/useDocumentTitle';
import HomePage from '@/pages/HomePage';
import CheckoutPage from '@/pages/CheckoutPage';
import OrderConfirmation from '@/pages/OrderConfirmation';
import LegalPage from '@/pages/LegalPage';
import SupplierReleasePage from '@/pages/SupplierReleasePage';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminProducts from '@/pages/AdminProducts';
import AdminCategories from '@/pages/AdminCategories';
import AdminOrders from '@/pages/AdminOrders';
import AdminCMS from '@/pages/AdminCMS';
import AdminLegal from '@/pages/AdminLegal';
import AdminUsers from '@/pages/AdminUsers';
import AdminEmailTemplates from '@/pages/AdminEmailTemplates';
import AdminSettings from '@/pages/AdminSettings';
import AdminMedia from '@/pages/AdminMedia';
import AdminSuppliers from '@/pages/AdminSuppliers';
import api from '@/lib/api';

// Visitor Tracking (DSGVO-konform - nur anonymisierte ID)
const useVisitorTracking = () => {
  useEffect(() => {
    // Anonymisierte Visitor-ID generieren oder aus localStorage laden
    let visitorId = localStorage.getItem('gb_visitor_id');
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('gb_visitor_id', visitorId);
    }
    
    // Besuch erfassen (nur einmal pro Session)
    const sessionKey = 'gb_visit_tracked_' + new Date().toISOString().slice(0, 10);
    if (!sessionStorage.getItem(sessionKey)) {
      api.post('/track-visit', new URLSearchParams({ visitor_id: visitorId }))
        .then(() => sessionStorage.setItem(sessionKey, 'true'))
        .catch(() => {}); // Fehler ignorieren - Tracking ist nicht kritisch
    }
  }, []);
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { token, loading, admin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-[#0E518D] border-t-transparent rounded-full" /></div>;
  if (!token) return <Navigate to="/admin/login" replace />;
  if (adminOnly && admin?.role !== 'admin') return <Navigate to="/admin/cms" replace />;
  return <AdminLayout>{children}</AdminLayout>;
};

const AdminRedirect = () => {
  const { token, loading, admin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-[#0E518D] border-t-transparent rounded-full" /></div>;
  if (!token) return <Navigate to="/admin/login" replace />;
  return <Navigate to={admin?.role === 'admin' ? "/admin/dashboard" : "/admin/cms"} replace />;
};

function App() {
  // Visitor Tracking aktivieren
  useVisitorTracking();
  
  useEffect(() => {
    // Disable browser scroll restoration, always start at top
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);

    // Hide emergent badge using CSS injection (safer than DOM manipulation)
    const style = document.createElement('style');
    style.id = 'hide-emergent-badge';
    style.textContent = '#emergent-badge { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }';
    if (!document.getElementById('hide-emergent-badge')) {
      document.head.appendChild(style);
    }
    
    return () => {
      const existingStyle = document.getElementById('hide-emergent-badge');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <DocumentTitle />
      <SettingsProvider>
      <AuthProvider>
        <CartProvider>
          <Header />
          <CartDrawer />
          <Routes>
            <Route path="/" element={<><HomePage /><Footer /></>} />
            <Route path="/bestellen" element={<><CheckoutPage /><Footer /></>} />
            <Route path="/bestellung/:orderId" element={<><OrderConfirmation /><Footer /></>} />
            <Route path="/lieferanten-freigabe/:token" element={<SupplierReleasePage />} />
            <Route path="/:page" element={<><LegalPage /><Footer /></>} />
            <Route path="/admin" element={<AdminRedirect />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/produkte" element={<ProtectedRoute adminOnly><AdminProducts /></ProtectedRoute>} />
            <Route path="/admin/kategorien" element={<ProtectedRoute adminOnly><AdminCategories /></ProtectedRoute>} />
            <Route path="/admin/bestellungen" element={<ProtectedRoute adminOnly><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/lieferanten" element={<ProtectedRoute adminOnly><AdminSuppliers /></ProtectedRoute>} />
            <Route path="/admin/cms" element={<ProtectedRoute><AdminCMS /></ProtectedRoute>} />
            <Route path="/admin/rechtliches" element={<ProtectedRoute><AdminLegal /></ProtectedRoute>} />
            <Route path="/admin/email-vorlagen" element={<ProtectedRoute adminOnly><AdminEmailTemplates /></ProtectedRoute>} />
            <Route path="/admin/medien" element={<ProtectedRoute adminOnly><AdminMedia /></ProtectedRoute>} />
            <Route path="/admin/benutzer" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/einstellungen" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
          </Routes>
          <CookieBanner />
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
